import { ArrowRight, MessageCircle } from "lucide-react";
import { IMAGES, SITE, buildWhatsAppUrl } from "@/lib/site";

const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Background images - mobile & desktop */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source media="(min-width: 768px)" srcSet={IMAGES.heroLaptop} />
          <img
            src={IMAGES.heroMobile}
            alt="Navilukallu Homestay at night, glowing lights and lush garden"
            className="w-full h-full object-cover ken-burns"
            fetchPriority="high"
          />
        </picture>
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#1B4332]/75 via-[#1B4332]/35 to-[#0f0f0f]/90" />
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#1A1A1A]/70 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 min-h-[100svh] flex flex-col justify-end pb-20 pt-32 sm:pt-40 lg:pt-32">
        <div className="max-w-3xl">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] tracking-[0.35em] uppercase text-[#D4AF37] font-medium">
              Malnad • Western Ghats • Karnataka
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="reveal font-heading text-white text-[3rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[5.5rem] font-medium tracking-tight"
            style={{ animationDelay: "0.1s" }}
          >
            Navilukallu
            <span className="block italic font-light text-[#E8C866]">
              Homestay
            </span>
          </h1>

          <p
            className="reveal mt-6 font-heading italic text-white/90 text-lg sm:text-xl md:text-2xl tracking-wide"
            style={{ animationDelay: "0.25s" }}
          >
            {SITE.tagline}
          </p>

          <p
            className="reveal mt-5 max-w-2xl text-white/80 text-sm sm:text-base md:text-lg leading-relaxed font-light"
            style={{ animationDelay: "0.4s" }}
          >
            Escape to the heart of the Western Ghats between{" "}
            Belur–Sakleshpur. Wake up amidst lush coffee estates, experience
            authentic Malnad hospitality, savor traditional homemade cuisine,
            and create unforgettable memories with your loved ones.
          </p>

          <div
            className="reveal mt-10 flex flex-wrap items-center gap-4"
            style={{ animationDelay: "0.55s" }}
          >
            <a
              href="#booking"
              data-testid="hero-book-now-button"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-[#1A1A1A] text-sm sm:text-base font-medium tracking-wide hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300"
            >
              Book Now
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href={buildWhatsAppUrl("Hi Navilukallu, I want to plan a stay. Please share availability.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md text-white border border-white/25 hover:bg-white/20 transition-all duration-300 text-sm sm:text-base"
            >
              <MessageCircle size={18} />
              WhatsApp Us
            </a>
          </div>

          {/* Stats strip */}
          <div
            className="reveal mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/15 pt-6"
            style={{ animationDelay: "0.7s" }}
          >
            {[
              { k: "5★", v: "Guest Rated" },
              { k: "3+ acres", v: "Coffee Estate" },
              { k: "100%", v: "Malnad Cuisine" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-heading text-2xl md:text-3xl text-[#D4AF37]">
                  {s.k}
                </div>
                <div className="text-white/60 text-xs sm:text-sm tracking-wide">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute z-20 bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/60">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
